import LPCarousel from 'components/LandingPage/LPCarousel/LPCarousel';
import SeriesSteps from 'components/LandingPage/Steps/SeriesSteps';
import React from 'react';
import { Content, ContentfulContentTypes, LandingPageData, Theme } from 'store/landing/types';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import getIsLoggedIn from 'store/app/selector/getIsLoggedIn';
import { useLandingPageContext } from 'context/LandingPageContext';
import { MODE_SELECTOR_TYPES } from 'constants/app';
import OpLiveRecommendations from 'components/Common/op-ui-wrappers/OpLiveRecommendations/OpLiveRecommendations';
import OpPlusCampaignModal from 'components/Common/op-ui-wrappers/OpPlusCampaignModal/OpPlusCampaignModal';
import OpStickyBar from 'components/Common/OpStickyBar/OpStickyBar';
import BigBannerGroup from '../BigBannerGroup/BigBannerGroup';
import BigBanner from '../BigBanner/BigBanner';
import DownloadApp from '../DownloadApp/DownloadApp';
import HTMLContent from '../HTMLContent/HTMLContent';
import LPCatalog from '../LPCatalog/LPCatalog';
import LPHero from '../LPHero/LPHero';
import LPSeparator from '../LPSeparator/LPSeparator';
import MediumBlockGroup from '../MediumBlockGroup/MediumBlockGroup';
import SpecialOffersGroup from '../SpecialOffersGroup/SpecialOffersGroup';
import WhiteboxBanner from '../WhiteboxBanner/WhiteboxBanner';
import RecommendationsBlock from '../Recommendations/RecommendationsBlock';
import styles from './LandingPageBuilder.module.scss';
import PixleeWidgetBlock from '../PixleeWidgetBlock/PixleeWidgetBlock';
import SmallBlockGroup from '../SmallBlockGroup/SmallBlockGroup';
import BigBlockGroup from '../BigBlockGroup/BigBlockGroup';
import MessageCardGroupBuilder from '../MessageCardGroup/MessageCardGroupBuilder/MessageCardGroupBuilder';
import Countdown from '../Countdown/Countdown';
import RecentlyViewedBlock from '../../Common/RecentlyViewed/RecentlyViewedBlock';
import LandingPageLastOrderDate from '../LandingPageLastOrderDate/LandingPageLastOrderDate';
import LPCatalogExtended from '../LPCatalog/LPCatalogExtended/LPCatalogExtended';
import HelloBar from '../Hellobar/HelloBar';
import SocialWidget from '../SocialWidget/SocialWidget';

type LandingPageBuilderProps = {
  landingPageData: LandingPageData;
  className?: string;
};

type OwnProps = {
  contentItem: Content;
};

const BuildBigBanner = ({ contentItem }: OwnProps) => {
  const { bigBanner } = contentItem;
  return (
    <BigBanner
      bannerImageUrl={bigBanner?.bannerImageUrl || ''}
      bannerImageUrlDesktop={bigBanner?.bannerImageUrlDesktop || ''}
      bannerImageUrlTablet={bigBanner?.bannerImageUrlTablet || ''}
      bannerImageUrlMobile={bigBanner?.bannerImageUrlMobile || ''}
      contentType={bigBanner?.contentType || ''}
      entryId={bigBanner?.entryId}
      reference={bigBanner?.reference}
      currentTargetDevice={bigBanner?.currentTargetDevice || ''}
      description={bigBanner?.description}
      descriptionFontWeight={bigBanner?.descriptionFontWeight}
      hideScrollIcon={bigBanner?.hideScrollIcon}
      key={bigBanner?.entryId}
      height={contentItem.bannerGroup.height}
      heightMobile={contentItem.bannerGroup.heightMobile}
      h1={bigBanner?.h1 || 'Placeholder'}
      h1Color={bigBanner?.h1Color || '#ffffff'}
      h1FontWeight={bigBanner?.h1FontWeight || 'bold'}
      h2={bigBanner?.h2 || 'Placeholder'}
      h2Color={bigBanner?.h2Color || '#000'}
      h2FontWeight={bigBanner?.h2FontWeight || 'normal'}
      buttonLabel={bigBanner?.buttonLabel || 'Placeholder'}
      buttonColor={bigBanner?.buttonColor || '#000000'}
      buttonBackgroundColor={bigBanner?.buttonBackgroundColor || '#ffffff'}
      buttonBorderColor={bigBanner?.buttonBorderColor || '#ffffff'}
      features={bigBanner?.features || []}
      theme={bigBanner?.theme || 'default'}
    />
  );
};

const BuildBigBannerGroup = ({ contentItem }: OwnProps) => {
  const [banner] = contentItem.bannerGroup?.banners;
  return (
    <BigBannerGroup
      entryId={banner?.entryId}
      features={banner?.features}
      height={contentItem.bannerGroup.height || 420}
      heightMobile={contentItem.bannerGroup.heightMobile}
      renderH1Desktop={banner?.renderH1Desktop}
      renderH1Mobile={banner?.renderH1Mobile}
      renderH2Desktop={banner?.renderH2Desktop}
      renderH2Mobile={banner?.renderH2Mobile}
      h1={banner?.h1}
      h1Color={banner?.h1Color}
      h1ColorPhone={banner?.h1ColorPhone}
      h1FontWeight={banner?.h1FontWeight || 'bold'}
      h2={banner?.h2}
      h2ColorPhone={banner?.h2ColorPhone}
      h2Color={banner?.h2Color}
      h2FontWeight={banner?.h2FontWeight || 'normal'}
      buttonLabel={banner?.buttonLabel}
      buttonColor={banner?.buttonColor}
      buttonBackgroundColor={banner?.buttonBackgroundColor || '#ffffff'}
      buttonBorderColor={banner?.buttonBorderColor || '#ffffff'}
      referenceUrl={banner?.referenceUrl || ''}
      bannerImageUrl={banner?.bannerImageUrl || ''}
      bannerImageUrlDesktop={banner?.bannerImageUrlDesktop || ''}
      bannerImageUrlTablet={banner?.bannerImageUrlTablet || ''}
      bannerImageUrlMobile={banner?.bannerImageUrlMobile || ''}
      theme={(banner?.theme as Theme) || ('default' as Theme)}
      appDownloadButtons={banner?.appDownloadButtons}
    />
  );
};

const BuildWhiteBoxBanner = ({ contentItem }: OwnProps) => {
  const { isDesktopDevice } = useLandingPageContext();
  const [banner] = contentItem.bannerGroup.banners;
  return (
    <WhiteboxBanner
      imageSrc={isDesktopDevice ? banner.bannerImageUrl : banner.bannerImageUrlMobile}
      buttonLabel={banner.buttonLabel || ''}
      h1={banner.h1 || ''}
      height={isDesktopDevice ? contentItem.bannerGroup.height : contentItem.bannerGroup.heightMobile}
      referenceUrl={banner.referenceUrl || ''}
    />
  );
};

const BuildCarousel = ({ contentItem }: OwnProps) => {
  return <LPCarousel contentItem={contentItem} />;
};

const BuildMediumBlockGroup = ({ contentItem }: OwnProps) => {
  if (!contentItem.mediumBlockGroup || !contentItem.mediumBlockGroup.blocks.length) {
    return null;
  }
  return (
    <MediumBlockGroup
      blocks={contentItem.mediumBlockGroup.blocks}
      component={contentItem.mediumBlockGroup.component}
      title={contentItem.mediumBlockGroup.title}
      variant={contentItem.mediumBlockGroup.variant}
      seeAllUrl={contentItem.mediumBlockGroup.seeAllUrl}
      seeAllLabel={contentItem.mediumBlockGroup.seeAllLabel}
      contentType={contentItem.contentType}
      currentTargetDevice={contentItem.currentTargetDevice}
      entryId={contentItem.entryId}
      name={contentItem.name}
    />
  );
};

const BuildSpecialOffersGroup = ({ contentItem }: OwnProps) => {
  return (
    <SpecialOffersGroup
      heading={contentItem.specialOffersGroup?.heading || ''}
      subheading={contentItem.specialOffersGroup?.subheading || ''}
      specialOffers={contentItem.specialOffersGroup.specialOffers || []}
    />
  );
};

const BuildHtmlContentBlock = ({ contentItem }: OwnProps) => {
  return <HTMLContent htmlContent={contentItem.html.content} />;
};

const BuildMessageCardGroupBlock = ({ contentItem }: OwnProps) => {
  return (
    <MessageCardGroupBuilder
      cards={contentItem.messageCardGroup.messageCards}
      messageCarGroupHeading={contentItem.messageCardGroup.heading}
      messageCardGroupTitle={contentItem.messageCardGroup.title}
    />
  );
};

const BuildLPCatalog = ({ contentItem }: OwnProps) => {
  const { catalog, entryId } = contentItem;
  if (!catalog) {
    return null;
  }

  const { groupedCatalogItems } = useLandingPageContext();
  const filteredItem = groupedCatalogItems?.filter((item) => item.entryId === entryId)[0];

  return MODE_SELECTOR_TYPES.includes(catalog.mode) ? (
    <LPCatalogExtended
      mode={catalog.mode}
      catalogItems={filteredItem?.items}
      defaultSelected={catalog.defaultSelected}
    />
  ) : (
    <LPCatalog name={catalog.title} items={catalog.items} variant={contentItem.catalog?.variant} />
  );
};

const BuildLPSeparator = ({ contentItem }: OwnProps) => {
  const { separator } = contentItem;
  if (!separator) {
    return null;
  }
  return (
    <LPSeparator
      paddingBottom={separator.paddingBottom}
      paddingBottomMobile={separator.paddingBottomPhone}
      paddingTop={separator.paddingTop}
      paddingTopMobile={separator.paddingTopPhone}
    />
  );
};

const BuildDownloadApp = ({ contentItem, layout }: OwnProps & Pick<LandingPageData, 'layout'>) => {
  const { downloadAppOrSendSms } = contentItem;
  if (!downloadAppOrSendSms) {
    return null;
  }
  return (
    <DownloadApp
      h1={downloadAppOrSendSms.h1}
      h1Desktop={downloadAppOrSendSms.h1Desktop}
      h1Mobile={downloadAppOrSendSms.h1Mobile}
      h1Tablet={downloadAppOrSendSms.h1Tablet}
      h2={downloadAppOrSendSms.h2}
      h2Desktop={downloadAppOrSendSms.h2Desktop}
      h2Mobile={downloadAppOrSendSms.h2Mobile}
      h2Tablet={downloadAppOrSendSms.h2Tablet}
      h3={downloadAppOrSendSms.h3}
      h3Desktop={downloadAppOrSendSms.h3Desktop}
      h3Mobile={downloadAppOrSendSms.h3Mobile}
      h3Tablet={downloadAppOrSendSms.h3Tablet}
      name={downloadAppOrSendSms.name}
      phoneAndroidImageUrl={downloadAppOrSendSms.phoneAndroidImageUrl}
      phoneImageUrl={downloadAppOrSendSms.smsPhoneBigImageUrl}
      sendAnotherSms={downloadAppOrSendSms.sendAnotherSms}
      smsButtonLabel={downloadAppOrSendSms.sendSmsButton.label}
      buttonNativeApp={downloadAppOrSendSms.downloadAppButton}
      layout={layout}
    />
  );
};

const BuildLandingPageLastOrderDateBlock = ({ contentItem }: OwnProps) => {
  const { lastOrderDateBlock } = contentItem;
  if (!lastOrderDateBlock) {
    return null;
  }

  return <LandingPageLastOrderDate lastOrderDateBlockData={lastOrderDateBlock} />;
};

const BuildPixleeWidgetBlock = ({ contentItem }: OwnProps) => {
  const { pixleeWidget } = contentItem;

  if (!pixleeWidget) {
    return null;
  }
  return <PixleeWidgetBlock pixleeWidget={pixleeWidget} />;
};

const BuildRecommendations = ({ contentItem }: OwnProps) => {
  const isLoggedIn = useSelector(getIsLoggedIn);

  if (!isLoggedIn || !contentItem.recommendationBlock) {
    return null;
  }

  const { categoryId, slotName, title, subCategoryId, liveRecommendations, preset, description } =
    contentItem.recommendationBlock;

  if (liveRecommendations) {
    return (
      <OpLiveRecommendations
        block-name="Landing-page"
        variant="lp"
        preset={preset || 'lp'}
        headline={title}
        description={description}
      />
    );
  }

  return (
    <RecommendationsBlock categoryId={categoryId} slotName={slotName} title={title} subCategoryId={subCategoryId} />
  );
};

const BuildSmallBlockGroup = ({ contentItem }: OwnProps) => {
  const { smallBlockGroup } = contentItem;

  if (!smallBlockGroup) {
    return null;
  }

  return <SmallBlockGroup key={smallBlockGroup.entryId} blocks={smallBlockGroup.blocks} />;
};

const BuildBigBlockGroup = ({ contentItem }: OwnProps) => {
  const { bigBlockGroup } = contentItem;

  if (!bigBlockGroup) {
    return null;
  }

  return <BigBlockGroup key={bigBlockGroup.entryId} blocks={bigBlockGroup.blocks} />;
};

const LandingPageElement = ({ contentItem, layout }: OwnProps & Pick<LandingPageData, 'layout'>) => {
  if (contentItem.contentType === ContentfulContentTypes.landingPageBigBanner) {
    return <BuildBigBanner contentItem={contentItem} />;
  }

  if (
    contentItem.contentType === ContentfulContentTypes.landingPageBannerGroup &&
    contentItem.bannerGroup?.banners &&
    contentItem.bannerGroup?.banners.length === 1 &&
    contentItem.bannerGroup.banners[0].theme !== 'whitebox'
  ) {
    return <BuildBigBannerGroup contentItem={contentItem} />;
  }

  if (
    contentItem.contentType === ContentfulContentTypes.landingPageBannerGroup &&
    contentItem.bannerGroup?.banners &&
    contentItem.bannerGroup?.banners.length === 1 &&
    contentItem.bannerGroup.banners[0].theme === 'whitebox'
  ) {
    return <BuildWhiteBoxBanner contentItem={contentItem} />;
  }

  if (
    contentItem.contentType === ContentfulContentTypes.landingPageBannerGroup &&
    contentItem.bannerGroup?.banners &&
    contentItem.bannerGroup.banners.length > 1
  ) {
    return <BuildCarousel contentItem={contentItem} />;
  }
  if (contentItem.contentType === ContentfulContentTypes.landingPageMediumBlockGroup) {
    return <BuildMediumBlockGroup contentItem={contentItem} />;
  }

  if (contentItem.contentType === ContentfulContentTypes.landingPageSpecialOffersGroup) {
    return <BuildSpecialOffersGroup contentItem={contentItem} />;
  }

  if (contentItem.contentType === ContentfulContentTypes.landingPageHtml) {
    return <BuildHtmlContentBlock contentItem={contentItem} />;
  }

  if (contentItem.contentType === ContentfulContentTypes.landingPageMessageCardGroup) {
    return <BuildMessageCardGroupBlock contentItem={contentItem} />;
  }

  if (contentItem.contentType === ContentfulContentTypes.landingPageSteps) {
    return <SeriesSteps contentItem={contentItem} />;
  }

  if (contentItem.contentType === ContentfulContentTypes.landingPageCatalog) {
    return <BuildLPCatalog contentItem={contentItem} />;
  }

  if (contentItem.contentType === ContentfulContentTypes.landingPageSeparator) {
    return <BuildLPSeparator contentItem={contentItem} />;
  }

  if (contentItem.contentType === ContentfulContentTypes.landingPageDownloadAppOrSendSms) {
    return <BuildDownloadApp contentItem={contentItem} layout={layout} />;
  }

  if (contentItem.contentType === ContentfulContentTypes.pixleeWidget) {
    return <BuildPixleeWidgetBlock contentItem={contentItem} />;
  }

  if (contentItem.contentType === ContentfulContentTypes.blueShiftRecommendationBlock) {
    return <BuildRecommendations contentItem={contentItem} />;
  }

  if (contentItem.contentType === ContentfulContentTypes.landingPageSmallBlockGroup) {
    return <BuildSmallBlockGroup contentItem={contentItem} />;
  }

  if (contentItem.contentType === ContentfulContentTypes.landingPageBigBlockGroup) {
    return <BuildBigBlockGroup contentItem={contentItem} />;
  }

  if (contentItem.contentType === ContentfulContentTypes.landingPageLastOrderDate) {
    return <BuildLandingPageLastOrderDateBlock contentItem={contentItem} />;
  }

  return null;
};

const LandingPageBuilder = ({ landingPageData, className }: LandingPageBuilderProps) => {
  const { theme } = useLandingPageContext();
  const { isDesktopDevice } = useLandingPageContext();

  return (
    <>
      {landingPageData.stickyBar && landingPageData.stickyBar.text && <OpStickyBar variant="landing-page" />}
      <div className={cn(styles.container, className, styles[`theme-${theme}`])}>
        <div
          className={cn({
            [styles.containerWide]: landingPageData.layout === 'wide',
          })}
        >
          {landingPageData.helloBar && landingPageData.helloBar.text && (
            <HelloBar
              text={landingPageData.helloBar.text}
              layout={landingPageData.layout}
              backgroundColor={landingPageData.helloBar.backgroundColor}
              textColor={landingPageData.helloBar.textColor}
            />
          )}
          {landingPageData.bannerGroup && (
            <LPHero
              bannerGroup={landingPageData.bannerGroup}
              uniqueSellingPoints={landingPageData.uniqueSellingPoints}
              theme={theme}
            />
          )}
          {landingPageData.countdown && <Countdown countdown={landingPageData.countdown} />}
          {landingPageData.content.map((contentItem: Content, i) => (
            <LandingPageElement
              key={contentItem.entryId + contentItem.name + i}
              contentItem={contentItem}
              layout={landingPageData.layout}
            />
          ))}
          {theme === 'studio' && (
            <div className={styles.recentlyViewedBlock}>
              <RecentlyViewedBlock theme="studio" />
            </div>
          )}
          {landingPageData.showCampaignContent && <OpPlusCampaignModal />}
          {!isDesktopDevice && <SocialWidget />}
        </div>
      </div>
    </>
  );
};

export default LandingPageBuilder;
